import type {
	FloodedMinorSeasonCreatePayload,
	FloodedMinorSeasonData,
	FloodedMinorSeasonDeletePayload,
	FloodedMinorSeasonPayload,
	FloodedMinorSeasonResponse,
	FloodedRiceParent,
	FloodedRiceResponse,
	FloodedRiceUploadPayload,
} from "../../../pages/activityBuilder/modules/floodedRice/floodedRiceTypes";
import { convertEmptyStringsToNull } from "../../../pages/activityBuilder/modules/inputs/utils";
import { FEModules } from "../../../utils/moduleList";
import { FEModulesObj } from "../../../utils/moduleList";
import { apiSlice, publicApiSlice, TagTypes } from "../../apiSlice";
import { createModuleGetApi, createEntriesGetApi } from "./factories/createModuleApi";

const MODULE_TYPE = "FloodedRice";
const TAG: TagTypes = "FloodedRice";
const TAG_DEFAULTS: TagTypes = "FloodedRiceDefaults";
const ENDPOINT = FEModulesObj[FEModules.FloodedRice].path;

const ENTRY_MODULE_TYPE = "FloodedRiceMinorSeasons";
const TAG_ENTRY: TagTypes = "FloodedSeasons";
export const TAG_ENTRY_DEFAULTS: TagTypes = "FloodedRiceSeasonDefaults";
export const ENTRY_ENDPOINT = "flooded-rices-minor-seasons";

const floodedRiceApiSlice = createModuleGetApi<FloodedRiceParent>({
	apiSlice,
	path: ENDPOINT,
	moduleName: MODULE_TYPE,
	tag: TAG,
	tagDefaults: TAG_DEFAULTS,
});

const floodedRiceEntriesApiSlice = createEntriesGetApi<FloodedMinorSeasonData[]>({
	apiSlice,
	path: ENTRY_ENDPOINT,
	moduleName: ENTRY_MODULE_TYPE,
	tag: TAG_ENTRY,
	tagDefaults: TAG_ENTRY_DEFAULTS,
});

const publicFloodedRiceApiSlice = createModuleGetApi<FloodedRiceParent>({
	apiSlice: publicApiSlice,
	path: ENDPOINT,
	moduleName: MODULE_TYPE,
	tag: TAG,
	tagDefaults: TAG_DEFAULTS,
});

const publicFloodedRiceEntriesApiSlice = createEntriesGetApi<FloodedMinorSeasonData[]>({
	apiSlice: publicApiSlice,
	path: ENTRY_ENDPOINT,
	moduleName: ENTRY_MODULE_TYPE,
	tag: TAG_ENTRY,
	tagDefaults: TAG_ENTRY_DEFAULTS,
});

export const { useGetFloodedRiceQuery, useGetFloodedRiceDefaultsQuery } = floodedRiceApiSlice;
export const { useGetFloodedRiceMinorSeasonsQuery, useGetFloodedRiceMinorSeasonsDefaultsQuery } = floodedRiceEntriesApiSlice;

export const {
	useGetFloodedRiceQuery: usePublicGetFloodedRiceQuery,
	useGetFloodedRiceDefaultsQuery: usePublicGetFloodedRiceDefaultsQuery
} = publicFloodedRiceApiSlice;
export const {
	useGetFloodedRiceMinorSeasonsQuery: usePublicGetFloodedRiceMinorSeasonsQuery,
	useGetFloodedRiceMinorSeasonsDefaultsQuery: usePublicGetFloodedRiceMinorSeasonsDefaultsQuery
} = publicFloodedRiceEntriesApiSlice;

export const moduleApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		updateFloodedRice: builder.mutation<
			FloodedRiceResponse,
			FloodedRiceUploadPayload
		>({
			query({ data, activityId }) {
				const { id, land_use_change, ...rest } = data;
				const body = { ...rest, activity: activityId };
				convertEmptyStringsToNull(body);
				return { url: `${ENDPOINT}/${id}/`, method: "PUT", body };
			},
			invalidatesTags: (results, _error, { activityId }) => [
				{ type: TAG, id: results?.id ?? undefined },
				{ type: TAG_ENTRY, id: activityId },
				{ type: "ModulesStatuses", id: activityId },
				{ type: "ModuleHistory", id: results?.id ? `flooded-rices-${results.id}` : undefined },
				{ type: "ModuleResults", id: results?.id ? `flooded-rices-${results.id}` : undefined },
				{ type: 'ModuleResults', id: "land-use-changes" },
				{ type: TAG_DEFAULTS, id: results?.id ? results?.id : undefined },
			],
		}),
		updateFloodedRiceMinorSeason: builder.mutation<
			FloodedMinorSeasonResponse,
			FloodedMinorSeasonPayload
		>({
			query({ body }) {
				const { id, land_use_change, ...rest } = body;
				const payload = { ...rest };

				convertEmptyStringsToNull(payload);
				return {
					url: `flooded-rices-minor-seasons/${id}/`,
					method: "PUT",
					body: payload,
				};
			},
			invalidatesTags: (_results, _error, { body }) => [
				{ type: TAG_ENTRY_DEFAULTS, id: body.id ? body.id : undefined },
			],
		}),
		createFloodedRiceMinorSeason: builder.mutation<
			FloodedMinorSeasonResponse,
			FloodedMinorSeasonCreatePayload
		>({
			query({ body }) {
				return {
					url: "flooded-rices-minor-seasons/",
					method: "POST",
					body,
				};
			},
			invalidatesTags: (_results, _error, { activityId, body }) => [
				{ type: TAG_ENTRY, id: activityId },
				{ type: "ModulesStatuses", id: activityId },
				{ type: "ModuleResults", id: body.parent ? `flooded-rices-${body.parent}` : undefined },
				{ type: 'ModuleResults', id: "land-use-changes" },
				{ type: "ModuleHistory", id: body.parent ? `flooded-rices-${body.parent}` : undefined },
				{ type: TAG_ENTRY_DEFAULTS, id: body.parent ? `flooded-rices-${body.parent}` : undefined },
			],
		}),
		deleteFloodedRiceMinorSeason: builder.mutation<
			void,
			FloodedMinorSeasonDeletePayload
		>({
			query({ id }) {
				return {
					url: `flooded-rices-minor-seasons/${id}/`,
					method: "DELETE",
				};
			},
			invalidatesTags: (_results, _error, { activityId, parent, id }) => [
				{ type: TAG_ENTRY, id: activityId },
				{ type: "ModulesStatuses", id: activityId },
				{ type: "ModuleResults", id: parent ? `flooded-rices-${parent}` : undefined },
				{ type: 'ModuleResults', id: "land-use-changes" },
				{ type: "ModuleHistory", id: parent ? `flooded-rices-${parent}` : undefined },
				{ type: TAG_ENTRY_DEFAULTS, id: id },
			],
		}),
	}),
});

export const {
	useUpdateFloodedRiceMutation,
	useUpdateFloodedRiceMinorSeasonMutation,
	useDeleteFloodedRiceMinorSeasonMutation,
	useCreateFloodedRiceMinorSeasonMutation,
} = moduleApiSlice;
